import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { clsx } from 'clsx';
import { Badge, Button } from '@/components/ui';
import type { User } from '@/types/userManagement.types';

const LIST_COMPANY_LIMIT = 2;
const LIST_ROLE_LIMIT = 2;

interface UserCardProps {
  user: User;
  onEdit?: (user: User) => void;
  onDelete?: (user: User) => void;
  onResendInvite?: (user: User) => void;
}

function notifySummary(user: User): string | null {
  const days = [
    user.isNotifyMonday,
    user.isNotifyTuesday,
    user.isNotifyWednesday,
    user.isNotifyThursday,
    user.isNotifyFriday,
  ];
  const on = days.filter((d) => d === true).length;
  if (on === 0) return null;
  return `${on} weekday${on === 1 ? '' : 's'} notifications`;
}

function roleEntries(user: User): { key: string; name: string; fromCompany: boolean }[] {
  const direct = user.userGroups.map((g) => ({
    key: `u-${g.id}`,
    name: g.name,
    fromCompany: false,
  }));
  const inherited = (user.companyUserGroups ?? []).map((g) => ({
    key: `c-${g.id}`,
    name: g.name,
    fromCompany: true,
  }));
  return [...direct, ...inherited];
}

function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return 'Never';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}

export const UserCard: React.FC<UserCardProps> = ({ user, onEdit, onDelete, onResendInvite }) => {
  const [expanded, setExpanded] = useState(false);

  const statusVariant =
    user.inviteStatus === 'Accepted'
      ? 'success'
      : user.inviteStatus === 'Pending'
        ? 'warning'
        : user.inviteStatus === 'Declined'
          ? 'error'
          : 'default';

  const roles = roleEntries(user);
  const notifications = notifySummary(user);

  return (
    <div
      className={clsx(
        'overflow-hidden rounded-lg border border-gray-200 transition-all duration-200 dark:border-dark-700',
        expanded && 'mb-4 mt-2 shadow-lg ring-1 ring-gray-200 dark:ring-gray-700'
      )}
    >
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        className="flex w-full cursor-pointer items-center gap-2 bg-white px-2 py-1.5 pr-3 text-left transition-colors hover:bg-gray-50 dark:bg-dark-800 dark:hover:bg-dark-700"
      >
        <ChevronRight
          className={clsx(
            'h-5 w-5 flex-shrink-0 text-gray-400 transition-transform duration-200 dark:text-gray-500',
            expanded && 'rotate-90'
          )}
          aria-hidden
        />
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30">
          <span className="text-xs font-semibold text-primary-700 dark:text-primary-300">
            {user.name.charAt(0).toUpperCase()}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-sm font-semibold text-gray-900 dark:text-white">{user.name}</span>
            <Badge variant={statusVariant} size="sm">
              {user.inviteStatus}
            </Badge>
          </div>
          <p className="truncate text-xs text-gray-500 dark:text-gray-400">{user.email}</p>

          <div className="mt-1 flex items-center gap-1 overflow-hidden">
            {user.companies.length > 0 ? (
              <>
                {user.companies.slice(0, LIST_COMPANY_LIMIT).map((company) => (
                  <Badge key={company.id} variant="primary" size="sm" className="shrink-0">
                    {company.name}
                  </Badge>
                ))}
                {user.companies.length > LIST_COMPANY_LIMIT && (
                  <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
                    +{user.companies.length - LIST_COMPANY_LIMIT}
                  </span>
                )}
              </>
            ) : (
              <span className="text-xs text-gray-400 dark:text-gray-500">No companies</span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-1 overflow-hidden">
            {roles.length > 0 ? (
              <>
                {roles.slice(0, LIST_ROLE_LIMIT).map((r) => (
                  <Badge
                    key={r.key}
                    variant={r.fromCompany ? 'primary' : 'default'}
                    size="sm"
                    className="shrink-0"
                    icon={
                      r.fromCompany ? (
                        <svg viewBox="0 0 20 20" fill="currentColor" className="h-3 w-3">
                          <path
                            fillRule="evenodd"
                            d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : undefined
                    }
                  >
                    {r.name}
                  </Badge>
                ))}
                {roles.length > LIST_ROLE_LIMIT && (
                  <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
                    +{roles.length - LIST_ROLE_LIMIT}
                  </span>
                )}
              </>
            ) : (
              <span className="text-xs text-gray-400 dark:text-gray-500">No roles</span>
            )}
          </div>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-200 bg-gray-50 px-3 py-3 dark:border-dark-700 dark:bg-gray-900/50">
          <h4 className="mb-3 text-sm font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            User details
          </h4>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <DetailItem label="Last Login" value={formatDateTime(user.lastLoginAt)} />
            {user.whatsAppNumber && <DetailItem label="WhatsApp" value={user.whatsAppNumber} />}
            {notifications && <DetailItem label="Notifications" value={notifications} />}
          </div>

          <div className="mt-3">
            <p className="type-caption mb-2 font-medium text-gray-600 dark:text-gray-300">
              Companies
            </p>
            {user.companies.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {user.companies.map((company) => (
                  <Badge key={company.id} variant="primary" size="sm">
                    {company.name}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">None</p>
            )}
          </div>

          <div className="mt-3">
            <p className="type-caption mb-2 font-medium text-gray-600 dark:text-gray-300">Roles</p>
            {roles.length > 0 ? (
              <div className="flex flex-col gap-2">
                {user.userGroups.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {user.userGroups.map((group) => (
                      <Badge key={group.id} variant="default" size="sm">
                        {group.name}
                      </Badge>
                    ))}
                  </div>
                )}
                {(user.companyUserGroups?.length ?? 0) > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {user.companyUserGroups!.map((group) => (
                      <Badge
                        key={`company-${group.id}`}
                        variant="primary"
                        size="sm"
                        icon={
                          <svg viewBox="0 0 20 20" fill="currentColor" className="h-3 w-3">
                            <path
                              fillRule="evenodd"
                              d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z"
                              clipRule="evenodd"
                            />
                          </svg>
                        }
                      >
                        {group.name}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">None</p>
            )}
          </div>

          {(onEdit || onResendInvite || onDelete) && (
            <div className="mt-4 flex flex-wrap gap-2 border-t border-gray-200 pt-4 dark:border-dark-600">
              {onEdit && (
                <Button variant="primary" size="sm" onClick={() => onEdit(user)}>
                  Edit user
                </Button>
              )}
              {onResendInvite && user.inviteStatus === 'Pending' && (
                <Button variant="outline" size="sm" onClick={() => onResendInvite(user)}>
                  Resend invite
                </Button>
              )}
              {onDelete && (
                <Button variant="danger" size="sm" onClick={() => onDelete(user)}>
                  Delete user
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="type-caption mb-0.5 font-medium text-gray-600 dark:text-gray-300">{label}</p>
      <p className="text-sm text-gray-900 dark:text-gray-100">{value}</p>
    </div>
  );
}
